import { useEffect, useState } from "react";
import API from "../../services/api";
import {
  Pagination,
  Modal,
  Input,
  Typography,
  Spin,
  Button,
  App,
  Empty,
} from "antd";
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import "./AdminStallApprovals.css";

const { Title, Text } = Typography;

function AdminStallApprovals() {
  const [stalls, setStalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedStallId, setSelectedStallId] = useState(null);

  const { message } = App.useApp();

  /* ── Helpers ──────────────────────────────────────────────── */
  const openRejectModal = (id) => {
    setSelectedStallId(id);
    setIsRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectReason("");
    setSelectedStallId(null);
  };

  /* ── Fetch ────────────────────────────────────────────────── */
  const fetchPendingStalls = async (page = 1) => {
    try {
      setLoading(true);
      const res = await API.get(`/stalls/pending/all?page=${page}&limit=15`);
      setStalls(res.data.stalls || []);
      setTotal(res.data.total || 0);
      setCurrentPage(res.data.currentPage || 1);
    } catch {
      message.error("Failed to load pending stalls");
      setStalls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingStalls(currentPage);
  }, [currentPage]);

  /* ── Actions ──────────────────────────────────────────────── */
  const handleApprove = async (id) => {
    try {
      await API.put(`/stalls/approve/${id}`);
      message.success("Stall approved successfully");
      fetchPendingStalls(currentPage);
    } catch {
      message.error("Approval failed");
    }
  };

  const handleConfirmReject = async () => {
    try {
      await API.put(`/stalls/reject/${selectedStallId}`, {
        reason: rejectReason,
      });
      message.success("Stall rejected with reason");
      closeRejectModal();
      fetchPendingStalls(currentPage);
    } catch (err) {
      message.error(err.response?.data?.message || "Reject failed");
    }
  };

  /* ── Loader ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="sap-loader-shell">
        <Spin size="large" />
      </div>
    );
  }

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div className="sap-page-shell">
      {/* HEADER */}
      <div className="sap-topbar">
        <div className="sap-topbar__content">
          <Title level={3} className="sap-topbar__title">
            Stall Approvals
          </Title>
          <Text className="sap-topbar__subtitle">
            Review pending stall and kiosk listings
          </Text>
        </div>

        <div className="sap-topbar__count">
          <span className="sap-topbar__count-number">{stalls.length}</span>
          <span className="sap-topbar__count-label">Pending Review</span>
        </div>
      </div>

      {/* CONTENT */}
      {stalls.length === 0 ? (
        <Empty
          description="No pending requests"
          className="sap-empty-state"
        />
      ) : (
        <div className="sap-listing-grid">
          {stalls.map((stall) => (
            <div
              key={stall._id}
              className="sap-listing-card"
              onClick={() => navigate(`/stall/${stall.slug}`)}
            >
              {/* Image */}
              <div className="sap-listing-card__media">
                <img
                  alt={stall.title}
                  src={
                    stall.images?.[0] ||
                    "https://placehold.co/400x250?text=No+Image"
                  }
                  className="sap-listing-card__image"
                />

                <button
                  className="sap-listing-card__view-action"
                  aria-label="View stall"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/stall/${stall.slug}`);
                  }}
                >
                  <EyeOutlined />
                </button>

                <div className="sap-listing-card__status">
                  <span className="sap-listing-card__status-dot" />
                  Pending
                </div>
              </div>

              {/* Body */}
              <div className="sap-listing-card__body">
                <h5
                  className="sap-listing-card__title"
                  title={stall.title}
                >
                  {stall.title}
                </h5>

                <div className="sap-listing-card__price">
                  <span className="sap-listing-card__currency">₹</span>
                  <span className="sap-listing-card__amount">
                    {stall.pricing?.price?.toLocaleString() || 0}
                  </span>
                  <span className="sap-listing-card__period">
                    /{stall.durationDetails?.durationType || "listing"}
                  </span>
                </div>

                <div className="sap-listing-card__meta">
                  <span>
                    {stall.location?.city}, {stall.location?.state}
                  </span>
                  <span className="sap-listing-card__meta-separator">•</span>
                  <span>{stall.stallDetails?.areaSqFt} sqft</span>
                  <span className="sap-listing-card__meta-separator">•</span>
                  <span>{stall.stallDetails?.stallType}</span>
                </div>

                <div className="sap-listing-card__actions">
                  <Button
                    type="primary"
                    className="sap-action-btn sap-action-btn--approve"
                    icon={<CheckCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprove(stall._id);
                    }}
                  >
                    Approve
                  </Button>

                  <Button
                    danger
                    className="sap-action-btn sap-action-btn--reject"
                    icon={<CloseCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openRejectModal(stall._id);
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      {stalls.length > 0 && (
        <div className="sap-pagination-wrap">
          <Pagination
            current={currentPage}
            total={total}
            pageSize={15}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* REJECT MODAL */}
      <Modal
        title="Reject Listing"
        open={isRejectModalOpen}
        okText="Confirm Rejection"
        cancelText="Cancel"
        okButtonProps={{ disabled: !rejectReason.trim(), danger: true }}
        onOk={handleConfirmReject}
        onCancel={closeRejectModal}
        className="sap-reject-modal"
      >
        <div className="sap-reject-modal__body">
          <Text strong className="sap-reject-modal__label">
            Rejection Reason
          </Text>

          <Input.TextArea
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="sap-reject-modal__textarea"
          />

          {!rejectReason.trim() && (
            <p className="sap-reject-modal__error">
              Reason is required
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminStallApprovals;